import deepEqual from 'fast-deep-equal'
import { encode } from 'js-base64'
import { URL_CONNECTION_POLLING_COMMANDS, URL_CONNECTION_SSE, URL_CONNECTION_WS } from '../../server/src/urls'
import { postJson, withDefaultErrorHandler } from './utils'

export type ConnectionMethod = 'ws' | 'sse' | 'polling'

export const defaultConnectionMethods: readonly ConnectionMethod[] = ['ws', 'sse']

export const currentConnectionMethods = ((): readonly ConnectionMethod[] => {
  const isConnectionMethod = (m: unknown): m is ConnectionMethod => m === 'ws' || m === 'sse' || m === 'polling'
  try {
    const s = localStorage.getItem('zumeco:connection:method')
    const method = s && (JSON.parse(s) as unknown)
    if (Array.isArray(method) && method.length !== 0 && method.every(isConnectionMethod)) {
      return method
    }
  } catch {
    // ignore
  }
  return defaultConnectionMethods
})()

export const setConnectionMethods = (connectionMethods: readonly ConnectionMethod[]) => {
  if (deepEqual(defaultConnectionMethods, connectionMethods)) {
    localStorage.removeItem('zumeco:connection:method')
  } else {
    localStorage.setItem('zumeco:connection:method', JSON.stringify(connectionMethods))
  }
  location.reload()
}

interface ConnectOptions {
  readonly onConnect?: (connection: Connection) => void
  readonly onDisconnect: () => void
  readonly onMessage: (message: string) => void
}
interface Connection {
  readonly method: ConnectionMethod
  close(): void
  send(message: string): unknown
}

const createConnection: Record<Connection['method'], (doodleId: string, options: ConnectOptions) => Connection> = {
  ws: (doodleId, { onConnect, onDisconnect, onMessage }) => {
    const webSocket = new WebSocket(`ws${location.protocol === 'https:' ? 's' : ''}://${location.host}${URL_CONNECTION_WS(doodleId)}`)
    const connection: Connection = { method: 'ws', close: () => webSocket.close(), send: message => webSocket.send(message) }
    webSocket.onopen = event => {
      console.debug('websocket:opened', event)
      onConnect?.(connection)
    }
    webSocket.onclose = event => {
      console.debug('websocket:closed', event)
      webSocket.onopen = null
      webSocket.onclose = null
      webSocket.onerror = null
      webSocket.onmessage = null
      onDisconnect()
    }
    webSocket.onerror = event => {
      console.error('websocket:error', event)
      webSocket.close()
    }
    webSocket.onmessage = event => onMessage(event.data as string)
    return connection
  },
  sse: (doodleId, { onConnect, onDisconnect, onMessage }) => {
    const url = URL_CONNECTION_SSE(doodleId)
    const source = new EventSource(url)
    let sseId: string | undefined
    const connection: Connection = {
      method: 'sse',
      close: () => source.close(),
      send: async (message: string) => {
        if (!sseId || source.readyState !== EventSource.OPEN) {
          return
        }
        const response = await fetch(url, {
          method: 'POST',
          // Base64 エンコードします。生の utf-8 文字列だとサーバー側で正しく解釈できない場合があるためです（<path> の d を圧縮している影響と見られます）。
          body: encode(message),
          headers: { 'Content-Type': 'text/plain; charset=UTF-8', 'X-SSE-ID': sseId },
        })
        if (!response.ok) {
          console.error(response)
          throw Error()
        }
      },
    }
    source.addEventListener('x-sse-id', event => {
      sseId = (event as Event & { data: string }).data
      console.debug('sse:opened', event)
      onConnect?.(connection)
    })
    source.onerror = event => {
      console.error('sse:error', event)
      onDisconnect()
    }
    source.onmessage = event => onMessage(event.data as string)
    return connection
  },
  polling: (doodleId, { onConnect, onDisconnect, onMessage }) => {
    let connectionId: string | undefined
    let cursorId: string | undefined
    let appropriatePollingIntervalInSeconds: number | undefined
    let timerHandle: number | undefined
    const connection: Connection = {
      method: 'polling',
      close: () => clearTimeout(timerHandle),
      send: (message: string) => withDefaultErrorHandler(postJson(URL_CONNECTION_POLLING_COMMANDS(doodleId), { connectionId, message })),
    }
    const onError = () => {
      cursorId = undefined
      if (connectionId) {
        connectionId = undefined
        onDisconnect()
      }
    }
    const poll = async () => {
      try {
        const response = await fetch(URL_CONNECTION_POLLING_COMMANDS(doodleId, connectionId, cursorId))
        if (!response.ok) {
          return onError()
        }
        const body = (await response.json()) as {
          messages: string[]
          connectionId: string
          cursorId: string
          appropriatePollingIntervalInSeconds: number
        }
        if (!connectionId && body.connectionId) {
          console.debug('polling:opened')
          onConnect?.(connection)
        }
        connectionId = body.connectionId
        cursorId = body.cursorId
        appropriatePollingIntervalInSeconds = body.appropriatePollingIntervalInSeconds
        body.messages.forEach(m => onMessage(m))
      } catch {
        onError()
      } finally {
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        timerHandle = setTimeout(poll, (appropriatePollingIntervalInSeconds || 30) * 1000)
      }
    }
    void poll()
    return connection
  },
}

export const connect = (doodleId: string, methods: readonly ConnectionMethod[], options: ConnectOptions) => {
  let connection: Connection
  let methodIndex = 0
  const _connect = () => {
    const method = methods[methodIndex++]
    if (method) {
      connection = createConnection[method](doodleId, {
        onConnect: options.onConnect,
        onMessage: options.onMessage,
        onDisconnect: () => {
          options.onDisconnect()
          _connect()
        },
      })
    }
  }
  _connect()
  return {
    get method() {
      return connection.method
    },
    close: () => {
      methodIndex = methods.length
      connection.close()
    },
    send: (message: string) => connection.send(message),
  }
}
